import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { makeUrl, errorCallback } from '../constant';
import {
    SUPPLIER_ORDERS_URL,
    SUPPLIER_PRODUCT_STATUSES_URL,
    ADMIN_CREATE_SUPPLIER_ORDER,
    SUPPLIERS_URL,
    GET_PRODUCT_SEARCH,
    getFetchRequest,
    postFetchRequest,
    patchFetchRequest,
    deleteFetchRequest,
} from '../store/requests';
import AutobaseRunPanel from './AutobaseRunPanel';
import ClientsTableOverlayScroll from './ClientsTableOverlayScroll';
import OrderProductHintsLayer from './OrderProductHintsLayer';

const formatDateInput = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateDisplayRu = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('ru-RU'); // формат DD.MM.YYYY
};

const maskDeliveryDate = (value) => {
    const digits = String(value ?? '').replace(/\D/g, '').slice(0, 8);
    let masked = '';
    for (let i = 0; i < digits.length; i += 1) {
        if (i === 2 || i === 4) {
            masked += '.';
        }
        masked += digits[i];
    }
    return masked;
};

const formatDeliveryDateMask = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${day}.${month}.${year}`;
};

const parseDeliveryDateMask = (value) => {
    const match = String(value ?? '').match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
    ) {
        return null;
    }
    const monthText = String(month).padStart(2, '0');
    const dayText = String(day).padStart(2, '0');
    return `${year}-${monthText}-${dayText}T00:00:00`;
};

const deliveryDateCaret = (masked, digitsBefore) => {
    let pos = 0;
    let seen = 0;
    while (pos < masked.length && seen < digitsBefore) {
        if (/\d/.test(masked[pos])) {
            seen += 1;
        }
        pos += 1;
    }
    return pos;
};

const parseCurrencyToNumber = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const normalized = String(value).replace(/\s+/g, '').replace(',', '.');
    const num = parseFloat(normalized);
    return Number.isNaN(num) ? 0 : num;
};

const formatCurrencyRu = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    return num.toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const getSupplierRowKey = (product) => {
    if (product?.isDraft) {
        return `draft-${product.rowId || product.productId}`;
    }
    return `saved-${product.id}`;
};

const parseNumeric = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const text = String(value).replace(',', '.');
    const match = text.match(/-?\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : 0;
};

const getHintPartNumber = (hint) =>
    (hint.productPartNumber || hint.partNumber || hint.article || hint.code || '').trim();

const getHintManufacturer = (hint) =>
    (
        hint.productManufacturerName ||
        hint.manufacturerName ||
        hint.mfr ||
        (hint.manufacturer && hint.manufacturer.name) ||
        ''
    ).trim();

const formatProductFieldValue = (partNumber, manufacturerName) => {
    const part = (partNumber || '').trim();
    const manufacturer = (manufacturerName || '').trim();
    if (part && manufacturer) {
        return `${part} (${manufacturer})`;
    }
    return part;
};

const collectCategorySingulars = (products) => {
    const byId = new Map();
    (Array.isArray(products) ? products : []).forEach((product) => {
        const nameSingular = (product.categoryName || '').trim();
        if (!product.categoryId || !nameSingular) {
            return;
        }
        byId.set(product.categoryId, nameSingular);
    });
    return Array.from(byId, ([id, nameSingular]) => ({ id, nameSingular }));
};

const compareCategoryNames = (left, right) =>
    String(left?.categoryName || '').localeCompare(String(right?.categoryName || ''), 'ru', { sensitivity: 'base' });

const flattenCategories = (categories) => (
    [...(Array.isArray(categories) ? categories : [])]
        .sort(compareCategoryNames)
        .flatMap((parent) => {
            const parentCount = Number(parent.productsNumber);
            const parentSafeCount = Number.isFinite(parentCount) ? parentCount : 0;
            const parentOption = {
                id: parent.categoryId,
                name: parent.categoryName,
                singular: parent.categoryNameSingular || '',
                label: `${parent.categoryName} (${parentSafeCount})`,
            };
            const children = [...(parent.subCategories || [])]
                .sort(compareCategoryNames)
                .map((sub) => {
                    const count = Number(sub.productsNumber);
                    const safeCount = Number.isFinite(count) ? count : 0;
                    return {
                        id: sub.categoryId,
                        name: sub.categoryName,
                        singular: sub.categoryNameSingular || '',
                        label: `${parent.categoryName} / ${sub.categoryName} (${safeCount})`,
                    };
                });
            return [parentOption, ...children];
        })
);

const categoryDisplayName = (category) => (category.singular || '').trim() || category.name || '';

const collectProductCategories = (products) => {
    const byProduct = new Map();
    (Array.isArray(products) ? products : []).forEach((product) => {
        if (!product.categoryAssigned || !product.categoryId || !product.productId) {
            return;
        }
        if (String(product.productId).startsWith('draft-')) {
            return;
        }
        byProduct.set(product.productId, product.categoryId);
    });
    return Array.from(byProduct, ([productId, categoryId]) => ({ productId, categoryId }));
};

const mapCatalogItemToHint = (item) => {
    const manufacturer = item.manufacturer || {};
    return {
        productId: item.id || item.productId || null,
        productPartNumber: (item.partNumber || item.productPartNumber || '').trim(),
        productManufacturerId: manufacturer.id || item.manufacturerId || item.productManufacturerId || null,
        productManufacturerName: manufacturer.name || item.manufacturerName || item.productManufacturerName || '',
    };
};

const extractPartNumberHints = (response) => {
    const list = Array.isArray(response) ? response : [];
    return list.filter((hint) => getHintPartNumber(hint)).slice(0, 10);
};

const extractSearchHints = (response) => {
    const items = Array.isArray(response && response.items) ? response.items : [];
    return items.map(mapCatalogItemToHint).filter((hint) => getHintPartNumber(hint)).slice(0, 5);
};

const fetchOrderProductHints = (query, onSuccess, onError) => {
    const hintUrl = makeUrl([GET_PRODUCT_SEARCH, 'hint/', `?q=${encodeURIComponent(query)}`]);
    const searchUrl = makeUrl([GET_PRODUCT_SEARCH, `?q=${encodeURIComponent(query)}&offset=1&limit=5`]);
    const loadSearchFallback = () => {
        getFetchRequest(
            searchUrl,
            (searchResponse) => onSuccess(extractSearchHints(searchResponse)),
            onError
        );
    };

    getFetchRequest(
        hintUrl,
        (response) => {
            const partHints = extractPartNumberHints(response);
            if (partHints.length > 0) {
                onSuccess(partHints);
                return;
            }
            loadSearchFallback();
        },
        () => loadSearchFallback()
    );
};

const getDuplicateHintPartNumbers = (hints) => {
    const counts = {};
    (hints || []).forEach((hint) => {
        const partNumber = getHintPartNumber(hint).toLowerCase();
        if (!partNumber) {
            return;
        }
        counts[partNumber] = (counts[partNumber] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((partNumber) => counts[partNumber] > 1));
};

const formatOrderHintLabel = (hint, showManufacturer) => {
    const partNumber = getHintPartNumber(hint);
    const manufacturer = getHintManufacturer(hint);
    if (showManufacturer && manufacturer) {
        return `${partNumber} (${manufacturer})`;
    }
    return partNumber;
};

// Детальный просмотр заказа поставщика
const formatClientOrderLabel = (link) => {
    const qty = link?.productCount ?? link?.qty ?? '';
    const qtyText = qty !== '' && qty !== null ? ` (${qty} шт)` : '';
    return `${link?.orderNumber || 'Заказ клиента'}${qtyText}`;
};

const SupplierOrderDetail = ({
    order,
    onBack,
    statusOptions,
    onChangeStatus,
    isNew = false,
    onSubmitNewOrder,
    onAddProductsToOrder,
    onOrderUpdated,
    suppliers = [],
}) => {
    const [purchaseToggleState, setPurchaseToggleState] = useState({});
    const [statusSelection, setStatusSelection] = useState({});
    const navigate = useNavigate();

    const [supplierName, setSupplierName] = useState(order?.supplierName || '');
    const [selectedSupplierId, setSelectedSupplierId] = useState('');
    const [externalNumber, setExternalNumber] = useState(order?.externalNumber || order?.orderNumber || '');
    const [currency, setCurrency] = useState(order?.currency || 'RUB');
    const [addedProducts, setAddedProducts] = useState([]);
    const [hints, setHints] = useState([]);
    const [hintsFetched, setHintsFetched] = useState(false);
    const [activeDraftProductId, setActiveDraftProductId] = useState(null);
    const [searchState, setSearchState] = useState({ productId: null, query: '' });
    const [catalogAddModal, setCatalogAddModal] = useState({ open: false, partNumber: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categoriesList, setCategoriesList] = useState([]);
    const [rowOrder, setRowOrder] = useState([]);
    const [selectedRowKey, setSelectedRowKey] = useState(null);
    const [orderDirty, setOrderDirty] = useState(false);
    const [deliveryDateDrafts, setDeliveryDateDrafts] = useState({});
    const deliveryDateCaretRef = useRef(null);
    const productAnchorRef = useRef(null);
    const tableScrollRef = useRef(null);
    const scrollAfterAddRef = useRef(false);

    useEffect(() => {
        setExternalNumber(order?.externalNumber || order?.orderNumber || '');
    }, [order?.id, order?.externalNumber, order?.orderNumber]);

    useEffect(() => {
        setDeliveryDateDrafts({});
    }, [order?.id]);

    useLayoutEffect(() => {
        const caret = deliveryDateCaretRef.current;
        if (!caret) {
            return;
        }
        deliveryDateCaretRef.current = null;
        const input = document.getElementById(`delivery-date-${caret.productId}`);
        if (input && document.activeElement === input) {
            input.setSelectionRange(caret.pos, caret.pos);
        }
    }, [deliveryDateDrafts]);

    const orderedProducts = useMemo(() => {
        const base = Array.isArray(order?.products) ? order.products : [];
        const byKey = new Map();
        base.forEach((product) => byKey.set(getSupplierRowKey(product), product));
        addedProducts.forEach((product) => byKey.set(getSupplierRowKey(product), product));
        const ordered = rowOrder.map((key) => byKey.get(key)).filter(Boolean);
        if (ordered.length) {
            return ordered;
        }
        return [...base, ...addedProducts];
    }, [order?.products, addedProducts, rowOrder]);

    useEffect(() => {
        const base = Array.isArray(order?.products) ? order.products : [];
        const serverKeys = base.map(getSupplierRowKey);
        const draftKeys = addedProducts.map(getSupplierRowKey);
        setRowOrder((prev) => {
            if (!orderDirty && addedProducts.length === 0) {
                return serverKeys;
            }
            const alive = new Set([...serverKeys, ...draftKeys]);
            const kept = prev.filter((key) => alive.has(key));
            const keptSet = new Set(kept);
            const missingSaved = serverKeys.filter((key) => !keptSet.has(key));
            const missingDrafts = draftKeys.filter((key) => !keptSet.has(key));
            return [...kept, ...missingSaved, ...missingDrafts];
        });
    }, [order?.products, addedProducts, orderDirty]);

    useEffect(() => {
        if (selectedRowKey && !rowOrder.includes(selectedRowKey)) {
            setSelectedRowKey(null);
        }
    }, [rowOrder, selectedRowKey]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                setSelectedRowKey(null);
                return;
            }
            if (target.closest('[data-order-row]') || target.closest('[data-order-move-buttons]')) {
                return;
            }
            setSelectedRowKey(null);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, []);

    useLayoutEffect(() => {
        if (!scrollAfterAddRef.current) {
            return;
        }
        scrollAfterAddRef.current = false;
        tableScrollRef.current?.scrollToBottom();
    }, [addedProducts, rowOrder]);

    if (!order) return null;

    const baseProducts = Array.isArray(order.products) ? order.products : [];
    const products = orderedProducts;
    const hasDraftProducts = products.some((p) => p && p.isDraft);

    const effectiveCurrency = isNew
        ? currency
        : (order.currency || currency || 'RUB');
    const isRubCurrency = effectiveCurrency === 'RUB';

    const statusById = {};
    if (Array.isArray(statusOptions)) {
        statusOptions.forEach((s) => {
            if (s && s.id) {
                statusById[s.id] = s;
            }
        });
    }

    const openCustomerOrder = (orderId) => {
        if (!orderId) {
            return;
        }
        navigate(`/sales/orders?orderId=${encodeURIComponent(orderId)}`);
    };

    const handlePurchaseToggle = (productKey) => {
        setPurchaseToggleState((prev) => ({
            ...prev,
            [productKey]: !prev[productKey],
        }));
    };

    const handleStatusChange = (productKey, value, productId) => {
        setStatusSelection((prev) => ({
            ...prev,
            [productKey]: value,
        }));
        if (onChangeStatus && productId) {
            onChangeStatus(productId, value);
        }
    };

    const totalVat = products.reduce((acc, product) => {
        if (!isRubCurrency) {
            return acc;
        }
        if (product.isDraft) {
            const qty = parseNumeric(product.productCount ?? '');
            const price = parseNumeric(product.productPrice ?? '');
            if (!qty || !price) return acc;
            const lineTotal = qty * price;
            const vatPercent = 22;
            const vat = lineTotal * vatPercent / (100 + vatPercent);
            return acc + vat;
        }
        return acc + (product.vatAmount || 0);
    }, 0);

    const totalWithVat = products.reduce((acc, product) => {
        if (product.isDraft) {
            const qty = parseNumeric(product.productCount ?? '');
            const price = parseNumeric(product.productPrice ?? '');
            if (!qty || !price) return acc;
            const lineTotal = qty * price;
            return acc + lineTotal;
        }
        return acc + (product.sum || 0);
    }, 0);

    useEffect(() => {
        getFetchRequest(
            makeUrl(['/api/v1/category/']),
            (response) => setCategoriesList(Array.isArray(response) ? response : []),
            () => setCategoriesList([])
        );
    }, []);

    // Инициализация / сброс черновых строк для нового заказа поставщика
    useEffect(() => {
        if (!isNew) {
            // Как только заказ перестал быть новым (после проведения),
            // очищаем локальные черновики и состояние подсказок.
            setAddedProducts([]);
            setHints([]);
            setActiveDraftProductId(null);
            return;
        }
        setAddedProducts((prev) => {
            if (prev && prev.length > 0) {
                return prev;
            }
            const newId = `draft-${Date.now()}-1`;
            return [
                {
                    rowId: newId,
                    productId: newId,
                    productName: '',
                    productCount: '',
                    productPrice: '',
                    deliveryTerm: '',
                    isDraft: true,
                },
            ];
        });
    }, [isNew, order?.id]);

    const handleDraftProductChange = (productId, field, value) => {
        setAddedProducts((prev) =>
            prev.map((product) => {
                if (product.productId !== productId) return product;

                let nextValue = value;

                if (field === 'productCount') {
                    const prevRaw = product.productCount ?? '';
                    const prevDigits = String(prevRaw).replace(/\D/g, '');
                    const inputDigits = String(value).replace(/\D/g, '');
                    let digits = inputDigits;

                    if (prevDigits && inputDigits === prevDigits && String(value).length < String(prevRaw).length) {
                        digits = prevDigits.slice(0, -1);
                    }

                    nextValue = digits ? `${Number(digits)} шт.` : '';
                } else if (field === 'productPrice') {
                    nextValue = String(value).replace(/[^\d.,]/g, '');
                } else if (field === 'deliveryTerm') {
                    const prevRaw = product.deliveryTerm ?? '';
                    const prevDigits = String(prevRaw).replace(/\D/g, '');
                    const inputDigits = String(value).replace(/\D/g, '');
                    let digits = inputDigits;

                    if (prevDigits && inputDigits === prevDigits && String(value).length < String(prevRaw).length) {
                        digits = prevDigits.slice(0, -1);
                    }

                    nextValue = digits ? `${Number(digits)} раб. дней` : '';
                }

                const updated = { ...product, [field]: nextValue };

                if (field === 'productName') {
                    updated.manufacturerName = '';
                    updated.categoryName = '';
                    updated.categoryId = '';
                    updated.categoryAssigned = false;
                    updated.isPlaced = false;
                    setActiveDraftProductId(productId);
                    setSearchState({ productId, query: value });
                }

                return updated;
            })
        );
    };

    useEffect(() => {
        const { productId, query } = searchState;
        const trimmedQuery = (query || '').trim();

        if (!productId || trimmedQuery.length < 3) {
            setHints([]);
            setHintsFetched(false);
            return;
        }

        setHintsFetched(false);
        let cancelled = false;

        const timeoutId = setTimeout(() => {
            fetchOrderProductHints(
                trimmedQuery,
                (list) => {
                    if (cancelled) {
                        return;
                    }
                    setHints(list);
                    setHintsFetched(true);
                },
                (error) => {
                    if (cancelled) {
                        return;
                    }
                    console.error('Ошибка загрузки подсказок по товару (закупки):', error);
                    setHints([]);
                    setHintsFetched(true);
                }
            );
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [searchState.productId, searchState.query]);

    const handleHintClick = (hint) => {
        if (!activeDraftProductId || !hint) return;
        const partNumber = getHintPartNumber(hint);
        const realProductId = hint.productId || hint.id || activeDraftProductId;
        setAddedProducts((prev) =>
            prev.map((product) => {
                if (product.productId !== activeDraftProductId) return product;
                return {
                    ...product,
                    productId: realProductId,
                    productName: partNumber || product.productName || '',
                    manufacturerName: getHintManufacturer(hint),
                    categoryName: '',
                    categoryId: '',
                    categoryAssigned: false,
                    isPlaced: true,
                };
            })
        );
        setHints([]);
        setHintsFetched(false);
        if (!hint.productId && !hint.id) {
            return;
        }
        getFetchRequest(
            makeUrl(['/api/v1/category/by-product/', realProductId, '/']),
            (data) => {
                setAddedProducts((prev) =>
                    prev.map((product) => {
                        if (product.productId !== realProductId) return product;
                        return {
                            ...product,
                            categoryId: data && data.categoryId ? data.categoryId : '',
                            categoryName: data && data.categoryName ? data.categoryName : '',
                        };
                    })
                );
            },
            (error) => {
                console.error('Ошибка загрузки категории товара:', error);
            }
        );
    };

    const handleAssignCategory = (productId, categoryId) => {
        const category = flattenCategories(categoriesList).find((item) => item.id === categoryId);
        if (!category) {
            return;
        }
        setAddedProducts((prev) =>
            prev.map((product) => {
                if (product.productId !== productId) return product;
                return {
                    ...product,
                    categoryId,
                    categoryName: categoryDisplayName(category),
                    categoryAssigned: true,
                };
            })
        );
    };

    const handleDraftCategoryChange = (productId, value) => {
        setAddedProducts((prev) =>
            prev.map((product) => {
                if (product.productId !== productId) return product;
                return { ...product, categoryName: value };
            })
        );
    };

    const closeProductHints = () => {
        setHints([]);
        setHintsFetched(false);
        setActiveDraftProductId(null);
    };

    const handleOpenCatalogAddModal = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setCatalogAddModal({
            open: true,
            partNumber: (searchState.query || '').trim(),
        });
        closeProductHints();
    };

    const handleCloseCatalogAddModal = () => {
        setCatalogAddModal({ open: false, partNumber: '' });
    };

    const handleAddProductRow = () => {
        const newId = `draft-${Date.now()}`;
        const rowKey = `draft-${newId}`;
        scrollAfterAddRef.current = true;
        setAddedProducts((prev) => [
            ...prev,
            {
                rowId: newId,
                productId: newId,
                productName: '',
                productCount: '',
                productPrice: '',
                deliveryTerm: '',
                isDraft: true,
            },
        ]);
        setRowOrder((prev) => (prev.includes(rowKey) ? prev : [...prev, rowKey]));
    };

    const handleRemoveSavedProduct = (productId, productName) => {
        if (!productId) {
            return;
        }
        const label = productName || 'этот товар';
        if (!window.confirm(`Удалить «${label}» из заказа?`)) {
            return;
        }
        deleteFetchRequest(
            makeUrl([SUPPLIER_ORDERS_URL, 'products/', productId, '/']),
            () => {
                if (onOrderUpdated) {
                    onOrderUpdated(order.id);
                }
            },
            () => {
                alert('Не удалось удалить товар из заказа.');
            }
        );
    };

    const moveSelectedRow = (direction) => {
        if (!selectedRowKey) {
            return;
        }
        const index = rowOrder.indexOf(selectedRowKey);
        const nextIndex = index + direction;
        if (index < 0 || nextIndex < 0 || nextIndex >= rowOrder.length) {
            return;
        }
        setRowOrder((prev) => {
            const currentIndex = prev.indexOf(selectedRowKey);
            const targetIndex = currentIndex + direction;
            if (currentIndex < 0 || targetIndex < 0 || targetIndex >= prev.length) {
                return prev;
            }
            const next = [...prev];
            const [moved] = next.splice(currentIndex, 1);
            next.splice(targetIndex, 0, moved);
            return next;
        });
        setOrderDirty(true);
    };

    const handleRemoveDraftProduct = (productId) => {
        setAddedProducts((prev) => {
            if (!prev || prev.length <= 1) {
                return prev;
            }
            return prev.filter((product) => product.productId !== productId);
        });
    };

    const handleSubmitNewOrder = () => {
        if (!isNew || isSubmitting) return;
        if (!supplierName.trim()) {
            alert('Пожалуйста, укажите поставщика перед проведением документа.');
            return;
        }
        if (!externalNumber.trim()) {
            alert('Пожалуйста, введите номер заказа поставщика.');
            return;
        }

        const productsPayload = (Array.isArray(orderedProducts) ? orderedProducts : [])
            .filter((p) => p.productId && !String(p.productId).startsWith('draft-'))
            .map((p) => ({
                productId: p.productId,
                productCount: parseNumeric(p.productCount ?? ''),
                productPrice: parseNumeric(p.productPrice ?? ''),
                deliveryTermDays: parseNumeric(p.deliveryTerm ?? ''),
            }));

        if (!productsPayload.length) {
            alert('Пожалуйста, добавьте хотя бы один товар в заказ поставщика.');
            return;
        }

        const body = {
            supplierName: supplierName,
            externalNumber: externalNumber || null,
            currency,
            products: productsPayload,
            categorySingulars: collectCategorySingulars(orderedProducts),
            productCategories: collectProductCategories(orderedProducts),
        };

        if (!onSubmitNewOrder) return;
        setIsSubmitting(true);
        onSubmitNewOrder(body, () => setIsSubmitting(false));
    };

    const handleDeliveryDateChange = (productId, event) => {
        const input = event.target;
        const raw = input.value;
        const selection = input.selectionStart ?? raw.length;
        const digitsBefore = raw.slice(0, selection).replace(/\D/g, '').length;
        const masked = maskDeliveryDate(raw);
        deliveryDateCaretRef.current = {
            productId,
            pos: deliveryDateCaret(masked, digitsBefore),
        };
        setDeliveryDateDrafts((prev) => ({
            ...prev,
            [productId]: masked,
        }));
    };

    const handleSubmitAddedProducts = () => {
        if (isNew || isSubmitting) return;

        const source = Array.isArray(orderedProducts) ? orderedProducts : [];
        const deliveryDateByProductId = {};
        for (const product of source) {
            if (product.isDraft || !product.id) {
                continue;
            }
            const draft = deliveryDateDrafts[product.id];
            if (draft === undefined) {
                continue;
            }
            const original = formatDeliveryDateMask(product.deliveryDate);
            if (draft === original) {
                continue;
            }
            const parsed = parseDeliveryDateMask(draft);
            if (!parsed) {
                alert('Дата поставки должна быть полностью заполнена в формате ДД.ММ.ГГГГ.');
                return;
            }
            deliveryDateByProductId[product.id] = parsed;
        }
        const datesChanged = Object.keys(deliveryDateByProductId).length > 0;

        const lineOrder = source.flatMap((product) => {
            if (product.isDraft) {
                if (!product.productId || String(product.productId).startsWith('draft-')) {
                    return [];
                }
                return [{
                    productId: product.productId,
                    productCount: Math.round(parseNumeric(product.productCount ?? '')),
                    productPrice: parseNumeric(product.productPrice ?? ''),
                    deliveryTermDays: parseNumeric(product.deliveryTerm ?? ''),
                }];
            }
            if (!product.id) {
                return [];
            }
            const line = {
                supplierOrderProductId: product.id,
            };
            if (deliveryDateByProductId[product.id]) {
                line.deliveryDate = deliveryDateByProductId[product.id];
            }
            return [line];
        });

        const hasNewProduct = lineOrder.some((line) => line.productId);
        const savedNumber = (order?.externalNumber || order?.orderNumber || '').trim();
        const nextNumber = externalNumber.trim();
        const numberChanged = nextNumber !== savedNumber;
        if (!nextNumber) {
            alert('Пожалуйста, введите номер заказа поставщика.');
            return;
        }
        const linesChanged = orderDirty || hasNewProduct || datesChanged;
        if (!linesChanged && !numberChanged) {
            alert('Нет изменений для сохранения.');
            return;
        }
        if (linesChanged && !lineOrder.length) {
            alert('Пожалуйста, добавьте хотя бы один товар в заказ поставщика.');
            return;
        }

        if (!onAddProductsToOrder || !order?.id) return;

        setIsSubmitting(true);
        onAddProductsToOrder(
            order.id,
            linesChanged ? lineOrder : [],
            collectCategorySingulars(source),
            collectProductCategories(source),
            numberChanged ? nextNumber : undefined,
            () => {
                setIsSubmitting(false);
                setOrderDirty(false);
                setSelectedRowKey(null);
                setAddedProducts([]);
                setDeliveryDateDrafts({});
            }
        );
    };

    // При создании нового заказа, если уже есть имя поставщика и список поставщиков загружен,
    // пробуем автоматически выбрать соответствующий элемент в селекте.
    useEffect(() => {
        if (!isNew) return;
        if (selectedSupplierId) return;
        if (!supplierName || !Array.isArray(suppliers) || !suppliers.length) return;
        const found = suppliers.find((s) => s.name === supplierName);
        if (found) {
            setSelectedSupplierId(found.id);
        }
    }, [isNew, supplierName, suppliers, selectedSupplierId]);

    const productHintsOpen = Boolean(activeDraftProductId) && (hints.length > 0 || hintsFetched);
    const productHints = productHintsOpen ? (
        <OrderProductHintsLayer anchorRef={productAnchorRef} onAnchorHidden={closeProductHints}>
            {hints.length > 0 ? (
                hints.map((hint, hintIndex) => {
                    const partNumber = getHintPartNumber(hint);
                    if (!partNumber) {
                        return null;
                    }
                    const duplicatePartNumbers = getDuplicateHintPartNumbers(hints);
                    const label = formatOrderHintLabel(
                        hint,
                        duplicatePartNumbers.has(partNumber.toLowerCase())
                    );
                    const hintKey = `${hint.productId || hint.id || partNumber}-${hintIndex}`;
                    return (
                        <div
                            key={hintKey}
                            className="order-product-hint-item"
                            onMouseDown={() => handleHintClick(hint)}
                        >
                            <span className="order-product-hint-part">
                                {label}
                            </span>
                        </div>
                    );
                })
            ) : (
                <div className="order-product-hint-item order-product-hint-item--missing">
                    <span className="order-product-hint-part order-product-hint-part--missing">
                        Нет в каталоге
                    </span>
                </div>
            )}
            <button
                type="button"
                className="order-product-hint-add"
                onMouseDown={handleOpenCatalogAddModal}
                aria-label="Добавить товар в каталог"
            >
                <span className="order-product-hint-add__icon">+</span>
            </button>
        </OrderProductHintsLayer>
    ) : null;

    return (
        <div className="order-detail-container">
            <div className="order-detail-header">
                <button className="order-detail-back-btn" onClick={onBack}>
                    <svg width="31" height="12" viewBox="0 0 31 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.219667 4.99262C-0.073225 5.28551 -0.073225 5.76039 0.219667 6.05328L4.99264 10.8263C5.28553 11.1191 5.76041 11.1191 6.0533 10.8263C6.34619 10.5334 6.34619 10.0585 6.0533 9.76559L1.81066 5.52295L6.0533 1.28031C6.34619 0.987415 6.34619 0.512542 6.0533 0.219648C5.76041 -0.073245 5.28553 -0.073245 4.99264 0.219648L0.219667 4.99262ZM30.75 5.52295V4.77295L0.749998 4.77295V5.52295V6.27295L30.75 6.27295V5.52295Z" fill="#666666" />
                    </svg>
                    Назад
                </button>
            </div>

            <div className="order-manager">
                <header className="order-header order-header--supplier-orders">
                    <div className="order-header__field">
                        <label className="order-header__label">Заказ поставщика:</label>
                        <input
                            type="text"
                            className="order-header__input order-header__input--supplier-orders"
                            value={externalNumber}
                            onChange={(e) => setExternalNumber(e.target.value)}
                        />
                    </div>

                    <div className="order-header__field">
                        <label className="order-header__label">От:</label>
                        <div className="order-header__input-wrapper">
                            <input
                                type="date"
                                className="order-header__input"
                                defaultValue={formatDateInput(order.dateCreated)}
                            />
                        </div>
                    </div>

                    <div className="order-header__field order-header__field--grow order-header__field--supplier-orders">
                        <label className="order-header__label">Поставщик:</label>
                        <div className="order-header__select-wrapper">
                            {isNew ? (
                                <select
                                    className="order-header__select"
                                    value={selectedSupplierId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSelectedSupplierId(id);
                                        const selected = Array.isArray(suppliers)
                                            ? suppliers.find((s) => s.id === id)
                                            : null;
                                        setSupplierName(selected ? selected.name : '');
                                    }}
                                >
                                    <option value="">Выберите поставщика</option>
                                    {Array.isArray(suppliers) &&
                                        suppliers.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                </select>
                            ) : (
                                <select className="order-header__select" value={order.supplierName || ''} readOnly>
                                    <option>{order.supplierName || ''}</option>
                                </select>
                            )}
                            <svg className="order-header__icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L5 5L9 1" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    </div>

                    
                </header>

                <footer className="order-footer order-footer--supplier-orders">
                    <div className="order-footer__actions">
                        {isNew ? (
                            <button
                                className="order-button"
                                type="button"
                                disabled={isSubmitting}
                                onClick={handleSubmitNewOrder}
                            >
                                {isSubmitting ? 'Сохранение...' : 'Провести документ'}
                            </button>
                        ) : (
                            <button
                                className="order-button"
                                type="button"
                                disabled={isSubmitting}
                                onClick={handleSubmitAddedProducts}
                            >
                                {isSubmitting ? 'Сохранение...' : 'Провести документ'}
                            </button>
                        )}
                        <button className="order-button">Поступление товаров</button>
                        <div className="order-move-buttons" data-order-move-buttons>
                            <button
                                type="button"
                                className="order-button order-move-button"
                                aria-label="Переместить товар вниз"
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={() => moveSelectedRow(1)}
                            >
                                <svg width="12" height="15" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.52344 0L4.77344 0L4.77344 15H5.52344H6.27344L6.27344 0L5.52344 0ZM4.99311 15.5303C5.286 15.8232 5.76087 15.8232 6.05377 15.5303L10.8267 10.7574C11.1196 10.4645 11.1196 9.98959 10.8267 9.6967C10.5338 9.40381 10.059 9.40381 9.76608 9.6967L5.52344 13.9393L1.2808 9.6967C0.987904 9.40381 0.51303 9.40381 0.220137 9.6967C-0.0727568 9.98959 -0.0727568 10.4645 0.220137 10.7574L4.99311 15.5303Z" fill="#222222"/>
                                </svg>
                            </button>
                            <button
                                type="button"
                                className="order-button order-move-button"
                                aria-label="Переместить товар вверх"
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={() => moveSelectedRow(-1)}
                            >
                                <svg width="12" height="15" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.52344 15.75H6.27344L6.27344 0.75H5.52344H4.77344L4.77344 15.75H5.52344ZM6.05377 0.21967C5.76087 -0.0732231 5.286 -0.0732231 4.99311 0.21967L0.220137 4.99264C-0.0727568 5.28553 -0.0727568 5.76041 0.220137 6.0533C0.51303 6.34619 0.987904 6.34619 1.2808 6.0533L5.52344 1.81066L9.76608 6.0533C10.059 6.34619 10.5338 6.34619 10.8267 6.0533C11.1196 5.76041 11.1196 5.28553 10.8267 4.99264L6.05377 0.21967Z" fill="#222222"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="order-footer__controls order-footer__controls--currency">
                        <label className="order-header__label">
                            Валюта:
                        </label>
                        <div className="order-header__select-wrapper">
                            {isNew ? (
                                <select
                                    className="order-header__select"
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                >
                                    <option value="RUB">RUB</option>
                                    <option value="CNY">CNY</option>
                                </select>
                            ) : (
                                <select
                                    className="order-header__select"
                                    value={effectiveCurrency}
                                    disabled
                                >
                                    <option value={effectiveCurrency}>{effectiveCurrency}</option>
                                </select>
                            )}
                            <svg className="order-header__icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L5 5L9 1" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    </div>
                </footer>
            </div>

            {/* Таблица товаров по заказу поставщика */}
            <div className="order-detail-products">
                <div className={`clients-table clients-table--order clients-table--supplier-order-lines ${(isNew || hasDraftProducts) ? 'clients-table--order-edit' : ''}`}>
                    <div className="clients-table__header">
                        <div className="clients-table__row clients-table__row--supplier-orders">
                            <div className="clients-table__cell clients-table__cell--id">№</div>
                            <div className="clients-table__cell">Товар</div>
                            <div className="clients-table__cell">Кол-во</div>
                            <div className="clients-table__cell">Цена</div>
                            <div className="clients-table__cell">Сумма</div>
                            <div className="clients-table__cell">НДС</div>
                            <div className="clients-table__cell">Срок пост.</div>
                            <div className="clients-table__cell">Заказан</div>
                            <div className="clients-table__cell">Дата пост.</div>
                            <div className="clients-table__cell">Осталось</div>
                            <div className="clients-table__cell clients-table__cell--ship">Приобрести</div>
                            <div className="clients-table__cell clients-table__cell--status-header">Статус товара</div>
                            <div className="clients-table__cell">Заказ клиента</div>
                            <div className="clients-table__cell">Резерв</div>
                        </div>
                    </div>
                    <ClientsTableOverlayScroll ref={tableScrollRef} overlay={productHints}>
                        {products.length === 0 ? (
                            <div className="clients-table__row">
                                <div className="clients-table__cell" style={{ gridColumn: '1 / -1' }}>
                                    Товары в заказе поставщика отсутствуют
                                </div>
                            </div>
                        ) : products.map((product, index) => {
                            const rowKey = getSupplierRowKey(product);
                            const productKey = rowKey;
                            const isRowSelected = selectedRowKey === rowKey;
                            const isPurchaseActive = Boolean(purchaseToggleState[productKey]);

                            const statusValue = statusSelection[productKey] || product.statusId || '';
                            const currentStatus = statusValue ? statusById[statusValue] : null;
                            const statusStyle = currentStatus && currentStatus.color
                                ? { backgroundColor: currentStatus.color, color: '#222222' }
                                : {};

                            const orderOptionsForProduct = !isNew
                                ? (Array.isArray(product.links)
                                    ? product.links.map((link) => ({
                                          orderProductId: link.orderProductId,
                                          orderId: link.orderId,
                                          orderNumber: link.orderNumber,
                                          qty: link.qty,
                                      }))
                                    : [])
                                // Для нового заказа поставщика до проведения
                                // селект «Заказ клиента» всегда показывает только «Не связан»
                                // и не подгружает список заказов.
                                : [];

                            return (
                                <div
                                    key={rowKey}
                                    className={`clients-table__row clients-table__row--supplier-orders${isRowSelected ? ' clients-table__row--selected' : ''}`}
                                    data-order-row
                                    onMouseDown={(event) => {
                                        const target = event.target;
                                        if (!(target instanceof Element)) {
                                            return;
                                        }
                                        const interactive = target.closest('input, button, select, textarea');
                                        if (interactive && !interactive.matches('input.order-product-input[readonly]')) {
                                            return;
                                        }
                                        setSelectedRowKey(rowKey);
                                    }}
                                >
                                    <div className="clients-table__cell clients-table__cell--id number-cell order-row-number">{index + 1}</div>
                                    <div className={`clients-table__cell ${product.isDraft ? 'clients-table__cell--product-edit clients-table__cell--product-input' : ''} ${product.categoryName ? 'clients-table__cell--product-with-category' : ''}`}>
                                        {product.isDraft ? (
                                            <div
                                                className={`order-product-input-wrapper${product.isPlaced ? ' order-product-input-wrapper--placed' : ''}`}
                                                ref={activeDraftProductId === product.productId ? productAnchorRef : null}
                                            >
                                                <input
                                                    type="text"
                                                    className="order-header__input order-product-input"
                                                    value={formatProductFieldValue(product.productName, product.manufacturerName)}
                                                    onChange={(e) =>
                                                        handleDraftProductChange(
                                                            product.productId,
                                                            'productName',
                                                            e.target.value
                                                        )
                                                    }
                                                    onBlur={() => {
                                                        setTimeout(closeProductHints, 100);
                                                    }}
                                                />
                                                {product.isPlaced && !product.categoryId ? (
                                                    <select
                                                        className="order-product-category order-product-category--select"
                                                        value=""
                                                        onChange={(e) => handleAssignCategory(product.productId, e.target.value)}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    >
                                                        <option value="">без категории</option>
                                                        {flattenCategories(categoriesList).map((category) => (
                                                            <option key={category.id} value={category.id}>
                                                                {category.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : product.isPlaced && (product.categoryId || product.categoryName) ? (
                                                    <input
                                                        type="text"
                                                        className="order-product-category"
                                                        value={product.categoryName || ''}
                                                        onChange={(e) => handleDraftCategoryChange(product.productId, e.target.value)}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                    />
                                                ) : null}
                                            </div>
                                        ) : (
                                            <div className={`order-product-input-wrapper${product.categoryName ? ' order-product-input-wrapper--placed' : ''}`}>
                                                <input
                                                    type="text"
                                                    readOnly
                                                    className="order-header__input order-product-input"
                                                    value={formatProductFieldValue(product.productName, product.manufacturerName)}
                                                />
                                                {product.categoryName ? (
                                                    <div className="order-product-category">{product.categoryName}</div>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                    <div className={`clients-table__cell ${product.isDraft ? 'clients-table__cell--product-input' : ''}`}>
                                        {product.isDraft ? (
                                            <input
                                                type="text"
                                                className="order-header__input order-product-input"
                                                value={product.productCount}
                                                onChange={(e) =>
                                                    handleDraftProductChange(product.productId, 'productCount', e.target.value)
                                                }
                                            />
                                        ) : (
                                            product.qty
                                        )}
                                    </div>
                                    <div className={`clients-table__cell ${product.isDraft ? 'clients-table__cell--product-input' : ''}`}>
                                        {product.isDraft ? (
                                            <input
                                                type="text"
                                                className="order-header__input order-product-input"
                                                value={product.productPrice}
                                                onChange={(e) =>
                                                    handleDraftProductChange(product.productId, 'productPrice', e.target.value)
                                                }
                                            />
                                        ) : (
                                            formatCurrencyRu(product.price)
                                        )}
                                    </div>
                                    <div className="clients-table__cell">
                                        {product.isDraft ? (() => {
                                            const qty = parseNumeric(product.productCount ?? '');
                                            const price = parseNumeric(product.productPrice ?? '');
                                            if (!qty || !price) return '';
                                            const lineTotal = qty * price;
                                            return formatCurrencyRu(lineTotal);
                                        })() : formatCurrencyRu(product.sum)}
                                    </div>
                                    <div className="clients-table__cell">
                                        {isRubCurrency
                                            ? (product.isDraft
                                                ? (() => {
                                                    const qty = parseNumeric(product.productCount ?? '');
                                                    const price = parseNumeric(product.productPrice ?? '');
                                                    if (!qty || !price) return '';
                                                    const lineTotal = qty * price;
                                                    const vatPercent = 22;
                                                    const vat = lineTotal * vatPercent / (100 + vatPercent);
                                                    return formatCurrencyRu(vat);
                                                })()
                                                : (product.vatAmount != null ? formatCurrencyRu(product.vatAmount) : '—'))
                                            : '—'}
                                    </div>
                                    <div className={`clients-table__cell ${product.isDraft ? 'clients-table__cell--product-input' : ''}`}>
                                        {product.isDraft ? (
                                            <input
                                                type="text"
                                                className="order-header__input order-product-input"
                                                value={product.deliveryTerm}
                                                onChange={(e) =>
                                                    handleDraftProductChange(product.productId, 'deliveryTerm', e.target.value)
                                                }
                                            />
                                        ) : product.deliveryTermDays != null ? (
                                            `${product.deliveryTermDays} раб. дн.`
                                        ) : (
                                            '—'
                                        )}
                                    </div>
                                    <div className="clients-table__cell">
                                        {/* Колонка "Заказан":
                                           - для черновика всегда "—"
                                           - для уже созданного заказа — дата создания строки */}
                                        {product.isDraft
                                            ? '—'
                                            : (product.orderedDate
                                                ? formatDateDisplayRu(product.orderedDate)
                                                : (order.dateCreated ? formatDateDisplayRu(order.dateCreated) : '—'))}
                                    </div>
                                    <div className={`clients-table__cell${!isNew && !product.isDraft ? ' clients-table__cell--delivery-date' : ''}`}>
                                        {!isNew && !product.isDraft ? (
                                            <input
                                                id={`delivery-date-${product.id}`}
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete="off"
                                                placeholder="дд.мм.гггг"
                                                className="order-header__input order-product-input order-product-input--date"
                                                value={
                                                    deliveryDateDrafts[product.id] !== undefined
                                                        ? deliveryDateDrafts[product.id]
                                                        : formatDeliveryDateMask(product.deliveryDate)
                                                }
                                                onChange={(e) => handleDeliveryDateChange(product.id, e)}
                                            />
                                        ) : (
                                            product.deliveryDate ? formatDateDisplayRu(product.deliveryDate) : '—'
                                        )}
                                    </div>
                                    <div
                                        className="clients-table__cell"
                                        style={
                                            !isNew &&
                                            product.remainingBusinessDays != null &&
                                            product.remainingBusinessDays >= 0 &&
                                            product.remainingBusinessDays < 5
                                                ? { color: 'red' }
                                                : undefined
                                        }
                                    >
                                        {isNew || product.remainingBusinessDays == null
                                            ? '—'
                                            : `${product.remainingBusinessDays} раб. дн.`}
                                    </div>
                                    <div className="clients-table__cell clients-table__cell--ship">
                                        <button
                                            type="button"
                                            className={`tristate-switch__thumb order-ship-toggle ${isPurchaseActive ? 'order-ship-toggle--active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handlePurchaseToggle(productKey);
                                            }}
                                            aria-pressed={isPurchaseActive}
                                            aria-label={`Приобрести ${product.toPurchase || ''}`}
                                        />
                                    </div>
                                    <div className="clients-table__cell cell-supplier-select">
                                        <select
                                            value={statusValue}
                                            style={statusStyle}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleStatusChange(productKey, e.target.value, product.id);
                                            }}
                                        >
                                            <option value="">Без статуса</option>
                                            {Array.isArray(statusOptions) && statusOptions.map((opt) => (
                                                <option key={opt.id} value={opt.id}>{opt.name}</option>
                                            ))}
                                        </select>
                                        <svg className="order-header__icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 1L5 5L9 1" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div className="clients-table__cell cell-supplier-select">
                                        {orderOptionsForProduct.length >= 2 ? (
                                            <>
                                                <select
                                                    value=""
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        const link = orderOptionsForProduct.find(
                                                            (opt) => opt.orderProductId === e.target.value
                                                        );
                                                        openCustomerOrder(link?.orderId);
                                                    }}
                                                >
                                                    <option value="" hidden>
                                                        {formatClientOrderLabel(orderOptionsForProduct[0])}
                                                    </option>
                                                    {orderOptionsForProduct.map((opt) => (
                                                        <option key={opt.orderProductId} value={opt.orderProductId}>
                                                            {formatClientOrderLabel(opt)}
                                                        </option>
                                                    ))}
                                                </select>
                                                <svg className="order-header__icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 1L5 5L9 1" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </>
                                        ) : orderOptionsForProduct.length === 1 ? (
                                            <div className="client-order-field">
                                                <button
                                                    type="button"
                                                    className="client-order-link"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openCustomerOrder(orderOptionsForProduct[0].orderId);
                                                    }}
                                                >
                                                    {orderOptionsForProduct[0].orderNumber || 'Заказ клиента'}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="client-order-field">Не связан</div>
                                        )}
                                    </div>
                                    <div className="clients-table__cell reserved">
                                        {product.reservedQty != null ? `${product.reservedQty} шт.` : '—'}
                                        {product.isDraft && addedProducts && addedProducts.length > 1 && (
                                            <button
                                                type="button"
                                                className="order-row-remove-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveDraftProduct(product.productId);
                                                }}
                                                aria-label="Удалить строку"
                                            >
                                                <span className="order-row-remove-btn__icon">×</span>
                                            </button>
                                        )}
                                        {!product.isDraft && product.id && baseProducts.length > 1 && (
                                            <button
                                                type="button"
                                                className="order-row-remove-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveSavedProduct(product.id, product.productName);
                                                }}
                                                aria-label="Удалить строку"
                                            >
                                                <span className="order-row-remove-btn__icon">×</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </ClientsTableOverlayScroll>
                </div>
            </div>

            <div className="order-new__bottom">
                <button
                    className="order-button"
                    type="button"
                    onClick={handleAddProductRow}
                >
                    Добавить товар +
                </button>
                <div className="order-summary">
                    {isRubCurrency && (
                        <div className="order-summary__row">
                            <span className="order-summary__label">НДС:</span>
                            <div className="order-summary__value-wrapper">
                                <span className="order-summary__value">
                                    {formatCurrencyRu(totalVat)}
                                </span>
                                <span className="order-summary__currency">{effectiveCurrency}</span>
                            </div>
                        </div>
                    )}
                    <div className="order-summary__row">
                        <span className="order-summary__label">
                            {isRubCurrency ? 'Всего с НДС:' : 'Всего:'}
                        </span>
                        <div className="order-summary__value-wrapper">
                            <span className="order-summary__value">
                                {formatCurrencyRu(totalWithVat)}
                            </span>
                            <span className="order-summary__currency">{effectiveCurrency}</span>
                        </div>
                    </div>
                </div>
            </div>
            {catalogAddModal.open && (
                <div className="order-product-hint-modal-overlay">
                    <div
                        className="order-product-hint-modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="order-product-hint-modal__header">
                            <span>
                                Добавление в каталог партномера {catalogAddModal.partNumber}
                            </span>
                            <button
                                type="button"
                                className="order-product-hint-modal__close"
                                onClick={handleCloseCatalogAddModal}
                                aria-label="Закрыть"
                            >
                                ×
                            </button>
                        </div>
                        <div className="order-product-hint-modal__body">
                            <AutobaseRunPanel
                                initialPartNumber={catalogAddModal.partNumber}
                                autoStart
                                onAddToOrder={handleCloseCatalogAddModal}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const handleDeleteSupplierOrder = (order, event, setOrders) => {
    event.stopPropagation();
    const orderNumber = order.externalNumber || order.orderNumber || '';
    const confirmed = window.confirm(
        `Удалить заказ поставщика № ${orderNumber}? Связи с заказами клиентов будут сняты, сами заказы клиентов останутся.`
    );
    if (!confirmed) return;
    deleteFetchRequest(
        makeUrl([SUPPLIER_ORDERS_URL, order.id, '/']),
        () => {
            setOrders((prev) => prev.filter((item) => item.id !== order.id));
        },
        () => {
            alert('Не удалось удалить заказ поставщика');
        }
    );
};

const PurchasesSupplierOrdersTab = ({ isCollapsed }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statusOptions, setStatusOptions] = useState([]);
    const [suppliers, setSuppliers] = useState([]);

    useEffect(() => {
        setIsLoading(true);
        setError(null);
        getFetchRequest(
            makeUrl([SUPPLIER_ORDERS_URL]),
            (data) => {
                setOrders(Array.isArray(data) ? data : []);
                setIsLoading(false);
            },
            (err) => {
                errorCallback(err);
                setError(err?.message || 'Не удалось загрузить заказы поставщиков');
                setIsLoading(false);
            }
        );

        getFetchRequest(
            makeUrl([SUPPLIER_PRODUCT_STATUSES_URL]),
            (data) => {
                setStatusOptions(Array.isArray(data) ? data : []);
            },
            (err) => {
                errorCallback(err);
            }
        );

        getFetchRequest(
            makeUrl([SUPPLIERS_URL]),
            (data) => {
                setSuppliers(Array.isArray(data) ? data : []);
            },
            (err) => {
                errorCallback(err);
            }
        );
    }, []);

    useEffect(() => {
        const orderId = searchParams.get('orderId');
        if (!orderId) {
            return;
        }
        getFetchRequest(
            makeUrl([SUPPLIER_ORDERS_URL, orderId, '/']),
            (data) => {
                setSelectedOrder(data);
                setSearchParams({}, { replace: true });
            },
            (err) => {
                errorCallback(err);
                alert('Не удалось загрузить заказ поставщика.');
                setSearchParams({}, { replace: true });
            }
        );
    }, [searchParams, setSearchParams]);

    const handleRowClick = (order) => {
        if (!order || !order.id) {
            return;
        }
        setSearchParams({ orderId: String(order.id) });
    };

    const handleChangeStatus = (productId, statusId) => {
        patchFetchRequest(
            makeUrl([SUPPLIER_ORDERS_URL, 'products/', productId, '/']),
            { statusId: statusId || null },
            () => {
                // локально обновим selectedOrder, чтобы сразу отразить изменение
                setSelectedOrder((prev) => {
                    if (!prev || !Array.isArray(prev.products)) return prev;
                    return {
                        ...prev,
                        products: prev.products.map((p) =>
                            p.id === productId ? { ...p, statusId } : p
                        ),
                    };
                });
            },
            (err) => {
                errorCallback(err);
            }
        );
    };

    const handleCreateSupplierOrderDraft = () => {
        const now = new Date();
        const draftOrder = {
            id: 'new',
            orderNumber: '',
            dateCreated: now.toISOString(),
            supplierName: '',
            currency: 'RUB',
            products: [],
            isNew: true,
        };
        setSelectedOrder(draftOrder);
    };

    const handleSubmitNewSupplierOrder = (body, done) => {
        postFetchRequest(
            makeUrl([ADMIN_CREATE_SUPPLIER_ORDER]),
            body,
            (created) => {
                if (done) done();
                // Обновляем список заказов поставщика
                getFetchRequest(
                    makeUrl([SUPPLIER_ORDERS_URL]),
                    (data) => {
                        setOrders(Array.isArray(data) ? data : []);
                    },
                    (err) => {
                        errorCallback(err);
                    }
                );
                setSelectedOrder({ ...created, isNew: false });
            },
            (err) => {
                console.error(err);
                alert('Не удалось создать заказ поставщика.');
                if (done) done();
            }
        );
    };

    const handleSupplierOrderUpdated = (orderId) => {
        if (!orderId) return;
        getFetchRequest(
            makeUrl([SUPPLIER_ORDERS_URL, orderId, '/']),
            (data) => {
                setSelectedOrder(data);
            },
            (err) => {
                errorCallback(err);
            }
        );
        getFetchRequest(
            makeUrl([SUPPLIER_ORDERS_URL]),
            (data) => {
                setOrders(Array.isArray(data) ? data : []);
            },
            (err) => {
                errorCallback(err);
            }
        );
    };

    const handleAddProductsToOrder = (orderId, lineOrder, categorySingulars, productCategories, externalNumber, done) => {
        if (!orderId) {
            if (done) done();
            return;
        }
        const body = {
            lineOrder: lineOrder || [],
            categorySingulars: categorySingulars || [],
            productCategories: productCategories || [],
        };
        if (externalNumber !== undefined) {
            body.externalNumber = externalNumber;
        }
        postFetchRequest(
            makeUrl([SUPPLIER_ORDERS_URL, orderId, '/products/']),
            body,
            (updated) => {
                setSelectedOrder(updated);
                if (done) done();
                // Перезагружаем список заказов поставщика, чтобы обновить сумму и валюту в таблице
                getFetchRequest(
                    makeUrl([SUPPLIER_ORDERS_URL]),
                    (data) => {
                        setOrders(Array.isArray(data) ? data : []);
                    },
                    (err) => {
                        errorCallback(err);
                    }
                );
            },
            (err) => {
                console.error(err);
                alert('Не удалось добавить товары в заказ поставщика.');
                if (done) done();
            }
        );
    };
    if (searchParams.get('orderId') && !selectedOrder) {
        return null;
    }
    return (
        <section className={`page-content ${isCollapsed === null ? '' : isCollapsed ? 'page-content_collapsed' : 'page-content_full-width'}`}>
            {selectedOrder ? (
                <SupplierOrderDetail
                    key={selectedOrder.id || (selectedOrder.isNew ? 'new' : 'draft')}
                    order={selectedOrder}
                    onBack={() => setSelectedOrder(null)}
                    statusOptions={statusOptions}
                    onChangeStatus={handleChangeStatus}
                    isNew={Boolean(selectedOrder && selectedOrder.isNew)}
                    onSubmitNewOrder={handleSubmitNewSupplierOrder}
                    onAddProductsToOrder={handleAddProductsToOrder}
                    onOrderUpdated={handleSupplierOrderUpdated}
                    suppliers={suppliers}
                />
            ) : (
                <article className="users">
                    <div className="clients-toolbar">
                        <button
                            type="button"
                            className="clients-toolbar__create"
                            onClick={handleCreateSupplierOrderDraft}
                        >
                            Создать заказ +
                        </button>
                        <div className="clients-toolbar__search">
                            <input type="text" placeholder="Поиск ...." />
                            <svg className="clients-toolbar__search-icon" width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.7938 14.6615C14.6355 14.6615 17.7498 11.5473 17.7498 7.70574C17.7498 3.86419 14.6355 0.75 10.7938 0.75C6.95218 0.75 3.83789 3.86419 3.83789 7.70574C3.83789 11.5473 6.95218 14.6615 10.7938 14.6615Z" stroke="#1C1C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M5.87594 12.6245L0.75 17.7503" stroke="#1C1C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    </div>
                    <div className="clients-table clients-table--supplier-orders">
                        <div className="clients-table__header">
                            <div className="clients-table__row">
                                <div className="clients-table__cell">Поставщик</div>
                                <div className="clients-table__cell">Номер заказа</div>
                                <div className="clients-table__cell">Дата</div>
                                <div className="clients-table__cell">Сумма</div>
                                <div className="clients-table__cell">Валюта</div>
                            </div>
                        </div>
                        <div className="clients-table__body">
                            {error && (
                                <div className="clients-table__row">
                                    <div className="clients-table__cell" style={{ gridColumn: '1 / -1' }}>
                                        {error}
                                    </div>
                                </div>
                            )}
                            {!error && isLoading && (
                                <div className="clients-table__row">
                                    <div className="clients-table__cell" style={{ gridColumn: '1 / -1' }}>
                                        Загрузка заказов поставщиков...
                                    </div>
                                </div>
                            )}
                            {!error && !isLoading && orders.length === 0 && (
                                <div className="clients-table__row">
                                    <div className="clients-table__cell" style={{ gridColumn: '1 / -1' }}>
                                        Заказы поставщиков не найдены
                                    </div>
                                </div>
                            )}
                            {!error && !isLoading && orders.length > 0 && (
                                orders.map((order) => (
                                    <div
                                        key={order.id}
                                        className="clients-table__row"
                                        onClick={() => handleRowClick(order)}
                                    >
                                        <div className="clients-table__cell">{order.supplierName || '—'}</div>
                                        <div className="clients-table__cell">{order.externalNumber || order.orderNumber || '—'}</div>
                                        <div className="clients-table__cell">
                                            {order.dateCreated ? formatDateInput(order.dateCreated) : '—'}
                                        </div>
                                        <div className="clients-table__cell">
                                            {order.totalAmount != null ? formatCurrencyRu(order.totalAmount) : '—'}
                                        </div>
                                        <div className="clients-table__cell">{order.currency || '—'}</div>
                                        <button
                                            type="button"
                                            className="order-row-remove-btn"
                                            onClick={(e) => handleDeleteSupplierOrder(order, e, setOrders)}
                                            aria-label="Удалить заказ"
                                        >
                                            <span className="order-row-remove-btn__icon">×</span>
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </article>
            )}
        </section>
    );
};

export default PurchasesSupplierOrdersTab;
